import { apiClient } from "@/auth-client";
import { UpsertDepartmentModal } from "@/components/modals/upsert-department-modal";
import { RemoveDepartment } from "@/components/remove-department";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  adminDepartmentsQueryOptions,
  sessionQueryOptions,
} from "@/lib/query-options";
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Building2,
  Gift,
  Loader2,
  MoreHorizontal,
  MoreVertical,
  Plus,
  Settings,
  Trash2,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

const companiesQueryOptions = queryOptions({
  queryKey: ["admin", "companies"],
  queryFn: async () => {
    const result = await apiClient.v1.admin.companies.$get();
    if (!result.ok) {
      throw new Error("Failed to fetch companies");
    }
    return result.json();
  },
});

export const Route = createFileRoute(
  "/(dashboard)/_authenticated/admin/_guard/companies",
)({
  loader: ({ context: { queryClient } }) => {
    return Promise.all([
      queryClient.ensureQueryData(sessionQueryOptions),
      queryClient.ensureQueryData(companiesQueryOptions),
    ]);
  },
  pendingComponent: () => (
    <div className="p-4 border rounded-md flex items-center space-x-2 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>Cargando empresas...</span>
    </div>
  ),
  component: CompaniesComponent,
});

function CompaniesComponent() {
  const { data: companiesData } = useSuspenseQuery(companiesQueryOptions);
  const queryClient = useQueryClient();

  // State for Create Company Dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [sendInviteEmailChecked, setSendInviteEmailChecked] = useState(false);

  // State for Delete Company Dialog
  const [companyToDelete, setCompanyToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");

  // State for Department Management Dialog
  const [departmentDialogCompany, setDepartmentDialogCompany] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const createCompanyMutation = useMutation({
    mutationFn: async (createData: {
      email: string;
      ownerName: string;
      companyName: string;
      sendInviteEmail: boolean;
    }) => {
      const result = await apiClient.v1.admin.companies.$post({
        json: {
          ownerEmail: createData.email,
          ownerName: createData.ownerName,
          companyName: createData.companyName,
          sendInviteEmail: createData.sendInviteEmail,
        },
      });
      if (!result.ok) {
        console.error("API Error creating company:", result.status);
        throw new Error("Este usuario ya es propietario de otra empresa.");
      }
    },
    onSuccess: (_, variables) => {
      toast.success(`Empresa ${variables.companyName} creada correctamente.`);
      setCompanyName("");
      setOwnerEmail("");
      setOwnerName("");
      setSendInviteEmailChecked(false);
      setIsCreateDialogOpen(false);

      queryClient.invalidateQueries({
        queryKey: companiesQueryOptions.queryKey,
      });
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear la empresa.");
    },
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const result = await apiClient.v1.admin.companies[":id"].$delete({
        param: { id: companyId },
      });
      if (!result.ok) {
        throw new Error("Error al eliminar la empresa.");
      }
    },
    onSuccess: () => {
      toast.success("Empresa eliminada correctamente.");
      queryClient.invalidateQueries({
        queryKey: companiesQueryOptions.queryKey,
      });
      closeDeleteDialog();
    },
    onError: (error) => {
      toast.error(error.message || "Error al eliminar la empresa.");
      closeDeleteDialog();
    },
  });

  const enableFreeTrialMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const result = await apiClient.v1.admin.companies[
        "enable-free-trial"
      ].$post({
        json: { companyId },
      });
      if (!result.ok) {
        const errorText = await result.text();
        throw new Error(`Failed to enable free trial: ${errorText}`);
      }
      return result.json();
    },
    onSuccess: () => {
      toast.success("Prueba gratuita activada exitosamente.");
      queryClient.invalidateQueries({
        queryKey: companiesQueryOptions.queryKey,
      });
    },
    onError: (error) => {
      toast.error(error.message || "Error al activar la prueba gratuita.");
    },
  });

  const openDeleteDialog = (company: { id: string; name: string }) => {
    setCompanyToDelete(company);
    setDeleteConfirmationInput("");
  };

  const closeDeleteDialog = () => {
    setCompanyToDelete(null);
    setDeleteConfirmationInput("");
  };

  const confirmDeleteCompany = () => {
    if (companyToDelete && deleteConfirmationInput === "delete") {
      deleteCompanyMutation.mutate(companyToDelete.id);
    }
  };

  const openDepartmentDialog = (company: { id: string; name: string }) => {
    setDepartmentDialogCompany(company);
    // Prefetch departments when opening dialog
    queryClient.ensureQueryData(adminDepartmentsQueryOptions(company.id));
  };

  const closeDepartmentDialog = () => {
    setDepartmentDialogCompany(null);
  };

  const handleCreateCompany = (e: FormEvent) => {
    e.preventDefault();
    if (!companyName || !ownerEmail || !ownerName) {
      toast.error(
        "Por favor ingresa el nombre de la empresa y el correo del propietario.",
      );
      return;
    }
    createCompanyMutation.mutate({
      email: ownerEmail,
      ownerName: ownerName,
      companyName: companyName,
      sendInviteEmail: sendInviteEmailChecked,
    });
  };

  return (
    <div className="p-4 space-y-6 max-w-7xl mx-auto">
      <div className="flex gap-2 mb-2">
        <Link to="/" className="inline-block">
          <Button variant="outline">Inicio</Button>
        </Link>
        <Link to="/admin" className="inline-block">
          <Button variant="outline">Panel administrador</Button>
        </Link>
      </div>
      <div>
        <h3 className="text-lg font-medium">Gestión de empresas</h3>
        <p className="text-sm text-muted-foreground">
          Gestiona empresas, sus departamentos y miembros.
        </p>
      </div>

      <div className="relative w-full overflow-auto">
        <div className="flex justify-end mb-4">
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Crear empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Crear nueva empresa</DialogTitle>
                <DialogDescription>
                  Ingresa los detalles a continuación para crear una nueva
                  empresa.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={handleCreateCompany}
                className="space-y-4 py-2 pb-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="company-name">Nombre de la empresa</Label>
                  <Input
                    id="company-name"
                    type="text"
                    placeholder="Empresa"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    disabled={createCompanyMutation.isPending}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-email">Correo del propietario</Label>
                  <Input
                    id="owner-email"
                    type="email"
                    placeholder="nombre@empresa.com"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    required
                    disabled={createCompanyMutation.isPending}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-name">Nombre del propietario</Label>
                  <Input
                    id="owner-name"
                    type="text"
                    placeholder="Propietario"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    required
                    disabled={createCompanyMutation.isPending}
                  />
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="send-invite-email-check"
                    checked={sendInviteEmailChecked}
                    onCheckedChange={(checked) =>
                      setSendInviteEmailChecked(Boolean(checked))
                    }
                    disabled={createCompanyMutation.isPending}
                  />
                  <Label
                    htmlFor="send-invite-email-check"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    ¿Quieres enviar un correo de invitación al propietario?
                  </Label>
                </div>
                <DialogFooter className="pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                    disabled={createCompanyMutation.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createCompanyMutation.isPending}
                  >
                    {createCompanyMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Crear
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Table>
          <TableCaption>Lista de empresas en el sistema.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Nombre</TableHead>
              <TableHead>Propietario</TableHead>
              <TableHead>Miembros</TableHead>
              <TableHead>Departamentos</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Creado</TableHead>
              <TableHead className="text-right min-w-[80px]">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companiesData.length > 0 ? (
              companiesData.map((company) => (
                <TableRow key={company.id}>
                  <TableCell className="font-medium">{company.name}</TableCell>
                  <TableCell>
                    <span className="underline">
                      {company.owner?.user?.email || "Sin propietario"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {company.memberCount}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {company.departmentCount}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {company.subscription?.plan || "Sin plan"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={company.subscription?.status || "Sin suscripción"}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(company.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-muted"
                        >
                          <span className="sr-only">Abrir menú</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <UpsertDepartmentModal
                          companyId={company.id}
                          isManager={false}
                          isAdmin={true}
                        >
                          <DropdownMenuItem asChild>
                            <button
                              type="button"
                              className="flex items-center w-full"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Añadir departamento
                            </button>
                          </DropdownMenuItem>
                        </UpsertDepartmentModal>
                        <DropdownMenuItem
                          onClick={() =>
                            openDepartmentDialog({
                              id: company.id,
                              name: company.name,
                            })
                          }
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Gestionar departamentos
                        </DropdownMenuItem>
                        {!company.subscription && company.owner?.user && (
                          <DropdownMenuItem
                            onClick={() =>
                              enableFreeTrialMutation.mutate(company.id)
                            }
                            disabled={enableFreeTrialMutation.isPending}
                          >
                            {enableFreeTrialMutation.isPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Gift className="mr-2 h-4 w-4" />
                            )}
                            Activar prueba gratuita
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={() =>
                            openDeleteDialog({
                              id: company.id,
                              name: company.name,
                            })
                          }
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Eliminar empresa
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  No se encontraron empresas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Department Management Dialog */}
      <Dialog
        open={!!departmentDialogCompany}
        onOpenChange={(open) => {
          if (!open) closeDepartmentDialog();
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Gestionar departamentos - {departmentDialogCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Ver y gestionar departamentos existentes para esta empresa.
            </DialogDescription>
          </DialogHeader>

          {departmentDialogCompany && (
            <DepartmentListContent companyId={departmentDialogCompany.id} />
          )}
        </DialogContent>
      </Dialog>

      {/* Company Delete Dialog */}
      <AlertDialog open={!!companyToDelete} onOpenChange={closeDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás absolutamente seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la empresa ({companyToDelete?.name}).
              <br />
              <span className="font-semibold text-foreground mt-2 block">
                Por favor escribe "delete" para confirmar.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            id="delete-confirm"
            value={deleteConfirmationInput}
            onChange={(e) => setDeleteConfirmationInput(e.target.value)}
            autoComplete="off"
            disabled={deleteCompanyMutation.isPending}
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeDeleteDialog}
              disabled={deleteCompanyMutation.isPending}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteCompany}
              disabled={
                deleteConfirmationInput !== "delete" ||
                deleteCompanyMutation.isPending
              }
            >
              {deleteCompanyMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Eliminar empresa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DepartmentListContent({
  companyId,
}: {
  companyId: string;
}) {
  const { data: departments } = useSuspenseQuery(
    adminDepartmentsQueryOptions(companyId),
  );

  return (
    <div className="space-y-4">
      {/* Departments List */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">
          Departamentos actuales ({departments?.length || 0})
        </h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {departments.map(
            (dept: {
              id: string;
              name: string;
              createdAt: string;
            }) => (
              <div
                key={dept.id}
                className="flex items-center justify-between p-3 bg-muted rounded-md hover:bg-muted/80 transition-colors"
              >
                <div className="flex-1">
                  <span className="text-sm font-medium">{dept.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    Creado: {new Date(dept.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <span className="sr-only">
                        Abrir acciones del departamento
                      </span>
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <RemoveDepartment
                        companyId={companyId}
                        departmentId={dept.id}
                      >
                        <button
                          type="button"
                          className="text-red-600 focus:bg-red-50 hover:bg-red-50 flex items-center px-2 py-1.5"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Eliminar departamento
                        </button>
                      </RemoveDepartment>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: string;
}) {
  const getStatusInfo = (status: string) => {
    switch (status) {
      case "trialing":
        return {
          text: "Prueba gratuita",
          className: "bg-blue-100 text-blue-800 border-blue-200",
        };
      case "active":
        return {
          text: "Activa",
          className: "bg-green-100 text-green-800 border-green-200",
        };
      case "past_due":
        return {
          text: "Pago atrasado",
          className: "bg-yellow-100 text-yellow-800 border-yellow-200",
        };
      case "canceled":
        return {
          text: "Cancelada",
          className: "bg-red-100 text-red-800 border-red-200",
        };
      case "incomplete":
        return {
          text: "Incompleta",
          className: "bg-orange-100 text-orange-800 border-orange-200",
        };
      case "incomplete_expired":
        return {
          text: "Expirada",
          className: "bg-red-100 text-red-800 border-red-200",
        };
      case "unpaid":
        return {
          text: "Sin pagar",
          className: "bg-red-100 text-red-800 border-red-200",
        };
      case "no_subscription":
        return {
          text: "Sin suscripción",
          className: "bg-gray-100 text-gray-800 border-gray-200",
        };
      default:
        return {
          text: "Desconocido",
          className: "bg-gray-100 text-gray-800 border-gray-200",
        };
    }
  };

  const statusInfo = getStatusInfo(status);

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${statusInfo.className}`}
    >
      {statusInfo.text}
    </span>
  );
}
